import { type CountryCode, getCountries, parsePhoneNumberFromString } from 'libphonenumber-js/max';

const SUPPORTED_COUNTRY_CODES = new Set<string>(getCountries());

function isSupportedCountryCode(countryCode: string): countryCode is CountryCode {
    return SUPPORTED_COUNTRY_CODES.has(countryCode);
}

export function resolvePhoneNumberCountryCode(
    countryCode: string | null | undefined,
    fallback: CountryCode = 'AU',
): CountryCode {
    const normalisedCountryCode = countryCode?.trim().toUpperCase();
    if (normalisedCountryCode && isSupportedCountryCode(normalisedCountryCode)) {
        return normalisedCountryCode;
    }

    return fallback;
}

/**
 * Validates a phone number.
 *
 * Returns `true` if the provided phone number is valid, `null` or `undefined`.
 * This means that `null` or `undefined` input is considered valid (useful for optional fields).
 *
 * @param phoneNumber - The phone number to validate
 * @param defaultCountryCode - Optional country code for local-format numbers
 * @returns `true` if the phone number is valid, `null` or `undefined`, otherwise `false`
 */
export function isPhoneNumberValid(phoneNumber: string | null | undefined, defaultCountryCode?: CountryCode): boolean {
    if (phoneNumber === null || phoneNumber === undefined) {
        return true;
    }
    return parsePhoneNumberFromString(phoneNumber, defaultCountryCode)?.isValid() ?? false;
}

export function normalisePhoneNumberToE164(
    phoneNumber: string | null | undefined,
    defaultCountryCode?: CountryCode,
): string | null {
    if (!phoneNumber) {
        return null;
    }

    const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, defaultCountryCode);
    return parsedPhoneNumber?.isValid() ? parsedPhoneNumber.number : null;
}

/**
 * A number an SMS can reach, in E.164: a valid mobile, or a number whose numbering plan cannot
 * tell mobiles from landlines (the US and Canada). Landlines and service numbers such as 1300
 * return null, so a caller skips the SMS instead of sending one the provider will refuse.
 */
export function normaliseMobileNumberToE164(
    phoneNumber: string | null | undefined,
    defaultCountryCode?: CountryCode,
): string | null {
    if (!phoneNumber) {
        return null;
    }

    const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, defaultCountryCode);
    if (!parsedPhoneNumber?.isValid()) {
        return null;
    }
    const type = parsedPhoneNumber.getType();
    return type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE' ? parsedPhoneNumber.number : null;
}

/** Whether an E.164 number belongs to the country, for callers that text only their own country. */
export function isPhoneNumberInCountry(phoneNumber: string, countryCode: CountryCode): boolean {
    return parsePhoneNumberFromString(phoneNumber)?.country === countryCode;
}
